import {
    Box, Button,
    Stack, TextField, Typography,
    FormControlLabel, Switch,
    FormControl, Select, MenuItem,
    Stepper, Step, StepLabel,
} from "@mui/material";
import {useEffect, useMemo, useRef, useState} from "react";
import QrStyleForm from "./QrStyleForm";
import {listDomains} from "../../Utils/domainsApi";
import {isValidUrl} from "../../Utils/urlUtils";

// Convertit un ISO string (UTC, tel que renvoyé par l'API) en valeur locale pour <input type="datetime-local">.
function toDatetimeLocalValue(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '';
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function QrCodeWizard({initial, onSubmit, onCancel}) {
    const isEditing = !!initial?.id;

    const [activeStep, setActiveStep] = useState(0);
    const [name, setName] = useState(initial?.name || '');
    const [targetUrl, setTargetUrl] = useState(initial?.targetUrl || '');
    const [trackingEnabled, setTrackingEnabled] = useState(initial?.trackingEnabled ?? true);
    const [ephemeralEnabled, setEphemeralEnabled] = useState(!!initial?.expiresAt || initial?.maxScans != null);
    const [isActive, setIsActive] = useState(initial?.isActive ?? true);
    const [domainId, setDomainId] = useState(initial?.domainId || '');
    const [domains, setDomains] = useState([]);
    const [saving, setSaving] = useState(false);

    const [expirationDateEnabled, setExpirationDateEnabled] = useState(!!initial?.expiresAt);
    const [expiresAt, setExpiresAt] = useState(toDatetimeLocalValue(initial?.expiresAt));
    const [maxScansEnabled, setMaxScansEnabled] = useState(initial?.maxScans != null);
    const [maxScans, setMaxScans] = useState(initial?.maxScans != null ? String(initial.maxScans) : '');

    const styleRef = useRef({...initial?.style, format: initial?.format});

    useEffect(() => {
        listDomains().then(setDomains).catch((error) => console.error('Erreur lors du chargement des domaines:', error));
    }, []);

    const steps = useMemo(() => [
        {key: 'informations', label: 'Informations'},
        {key: 'style', label: 'Style'},
        ...(trackingEnabled ? [{key: 'tracking', label: 'Suivi & domaine'}] : []),
        ...(ephemeralEnabled ? [{key: 'expiration', label: 'Expiration'}] : []),
    ], [trackingEnabled, ephemeralEnabled]);

    useEffect(() => {
        setActiveStep((step) => Math.min(step, steps.length - 1));
    }, [steps.length]);

    const targetUrlTouched = targetUrl.trim().length > 0;
    const targetUrlIsValid = isValidUrl(targetUrl);
    const currentStepKey = steps[activeStep]?.key;

    const expirationIsValid = !ephemeralEnabled || (
        (expirationDateEnabled && !!expiresAt) || (maxScansEnabled && parseInt(maxScans, 10) > 0)
    );

    const canGoNext = () => {
        if (currentStepKey === 'informations') return name.trim() && targetUrlIsValid;
        if (currentStepKey === 'expiration') return expirationIsValid;
        return true;
    };

    const handleNext = () => setActiveStep((step) => Math.min(step + 1, steps.length - 1));
    const handleBack = () => setActiveStep((step) => Math.max(step - 1, 0));

    const handleSubmit = async () => {
        setSaving(true);
        try {
            await onSubmit({
                name,
                targetUrl,
                trackingEnabled,
                isActive,
                domainId: trackingEnabled ? (domainId || null) : null,
                expiresAt: ephemeralEnabled && expirationDateEnabled && expiresAt ? new Date(expiresAt).toISOString() : null,
                maxScans: ephemeralEnabled && maxScansEnabled && maxScans ? parseInt(maxScans, 10) : null,
                ...styleRef.current,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Stepper activeStep={activeStep} sx={{mb: 4}}>
                {steps.map((step) => (
                    <Step key={step.key}><StepLabel>{step.label}</StepLabel></Step>
                ))}
            </Stepper>

            {currentStepKey === 'informations' && (
                <Stack spacing={3}>
                    <TextField label="Nom" fullWidth value={name} onChange={(e) => setName(e.target.value)}/>
                    <TextField
                        label="URL cible"
                        fullWidth
                        placeholder="https://exemple.com"
                        value={targetUrl}
                        onChange={(e) => setTargetUrl(e.target.value)}
                        error={targetUrlTouched && !targetUrlIsValid}
                        helperText={targetUrlTouched && !targetUrlIsValid ? 'URL invalide (doit commencer par http:// ou https://)' : ' '}
                    />
                    <FormControlLabel
                        control={<Switch checked={trackingEnabled} onChange={(e) => setTrackingEnabled(e.target.checked)}/>}
                        label="Suivi analytics (redirection via qrcode_server, log des scans)"
                    />
                    <FormControlLabel
                        control={<Switch checked={ephemeralEnabled} onChange={(e) => setEphemeralEnabled(e.target.checked)}/>}
                        label="QR code éphémère (expiration par date ou nombre de scans)"
                    />
                </Stack>
            )}

            {currentStepKey === 'style' && (
                <QrStyleForm initialValue={styleRef.current} onChange={(style) => { styleRef.current = style; }}/>
            )}

            {currentStepKey === 'tracking' && (
                <Stack spacing={3}>
                    {isEditing && (
                        <FormControlLabel
                            control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)}/>}
                            label="Actif (désactivez pour suspendre la redirection sans supprimer le QR code)"
                        />
                    )}

                    <Box>
                        <Typography variant="h6" gutterBottom>Domaine</Typography>
                        <FormControl fullWidth>
                            <Select value={domainId} displayEmpty onChange={(e) => setDomainId(e.target.value)}>
                                <MenuItem value="">Domaine par défaut de qrcode_server</MenuItem>
                                {domains.map((d) => (
                                    <MenuItem key={d.id} value={d.id}>{d.domain}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        Les statistiques de scan (appareil, navigateur, pays, ville, source, langue) sont collectées automatiquement
                        sous forme de compteurs agrégés, sans donnée individuelle conservée. L'IP n'est utilisée que pendant 24h,
                        sous forme de hash anonyme, pour distinguer un visiteur nouveau d'un visiteur récurrent.
                    </Typography>
                </Stack>
            )}

            {currentStepKey === 'expiration' && (
                <Stack spacing={3}>
                    <Typography variant="body2" color="text.secondary">
                        Désactivez automatiquement la redirection après une date donnée et/ou un nombre de scans maximum.
                    </Typography>

                    <Stack direction="row" alignItems="center" spacing={2}>
                        <FormControlLabel
                            sx={{minWidth: 220}}
                            control={<Switch checked={expirationDateEnabled} onChange={(e) => setExpirationDateEnabled(e.target.checked)}/>}
                            label="Date d'expiration"
                        />
                        {expirationDateEnabled && (
                            <TextField
                                type="datetime-local"
                                size="small"
                                value={expiresAt}
                                onChange={(e) => setExpiresAt(e.target.value)}
                                InputLabelProps={{shrink: true}}
                            />
                        )}
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={2}>
                        <FormControlLabel
                            sx={{minWidth: 220}}
                            control={<Switch checked={maxScansEnabled} onChange={(e) => setMaxScansEnabled(e.target.checked)}/>}
                            label="Nombre de scans maximum"
                        />
                        {maxScansEnabled && (
                            <TextField
                                type="number"
                                size="small"
                                value={maxScans}
                                onChange={(e) => setMaxScans(e.target.value)}
                                inputProps={{min: 1, step: 1}}
                                sx={{width: 140}}
                            />
                        )}
                    </Stack>

                    {!expirationIsValid && (
                        <Typography variant="body2" color="error">
                            Choisissez une date d'expiration valide et/ou un nombre de scans maximum.
                        </Typography>
                    )}
                </Stack>
            )}

            <Stack direction="row" justifyContent="space-between" sx={{mt: 4}}>
                <Button onClick={activeStep === 0 ? onCancel : handleBack}>
                    {activeStep === 0 ? 'Annuler' : 'Précédent'}
                </Button>
                {activeStep < steps.length - 1 ? (
                    <Button variant="contained" color="primary" onClick={handleNext} disabled={!canGoNext()}>
                        Suivant
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={saving || !expirationIsValid}
                    >
                        {saving ? 'Enregistrement...' : (isEditing ? 'Enregistrer' : 'Créer')}
                    </Button>
                )}
            </Stack>
        </Box>
    );
}

export default QrCodeWizard;
